import React from "react";
import { useState } from "react";
import { FiMail, FiAlertCircle, FiCheckCircle, FiInfo, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";

const GAMES = require("../data/games.json");

function Unsubscribe() {
    const [email, setEmail] = useState("");
    const [userId, setUserId] = useState("");
    const [selectedGames, setSelectedGames] = useState([]);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [consent, setConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGameToggle = (gameId) => {
        setSelectedGames(prev =>
            prev.includes(gameId)
                ? prev.filter(id => id !== gameId)
                : [...prev, gameId]
        );
    };

    const handleSelectAll = () => {
        const allGameIds = Object.values(GAMES).map(game => game.id);
        const allSelected = allGameIds.length > 0 && allGameIds.every(id => selectedGames.includes(id));
        setSelectedGames(allSelected ? [] : allGameIds);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email && !userId) {
            setError("Please provide either an email address or user ID.");
            return;
        }

        if (!consent) {
            setError("Please confirm your consent to unsubscribe.");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {};
            if (email) payload.email = email;
            if (userId) payload.userId = userId;
            if (selectedGames.length > 0) payload.games = selectedGames;

            const response = await fetch('/api/unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setDone(true);
            } else {
                setError("Failed to process unsubscribe request. Please try again or contact us directly.");
            }
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setError("Failed to process unsubscribe request. Please try again or contact us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="pt-24 pb-16 text-[#78350f] min-h-screen">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-block p-4 bg-red-600/20 rounded-full mb-4">
                        <FiMail className="text-red-400 text-4xl" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                        🗑️ Account Deletion Request Confirmation and Policy
                    </h1>
                    <p className="text-[#92400e] text-lg">
                        Thank you for submitting your <span class="font-bold">account deletion request</span>. Your request has been successfully placed in the processing queue.
                    </p>
                </div>

                {!done ? (
                    <div className="bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 md:p-8 shadow-lg border border-white/40 backdrop-blur-sm">
                        {/* Warning Notice */}
                        <div className="mb-6 p-4 bg-yellow-600/20 border border-yellow-500/30 rounded-lg flex items-start gap-3">
                            <FiAlertCircle className="text-yellow-400 text-xl flex-shrink-0 mt-0.5" />
                            <div className="text-[#78350f] text-sm">
                                <p className="font-semibold mb-1">Key Information: The 10-Day Retention Window</p>
                                <p className="text-[#92400e]">
                                    Please note that a <span class="font-bold">10-day data retention period</span> is in effect starting now.
                                </p>
                                <ul className="list-disc list-inside space-y-1 ml-2">
                                    <li className="text-[#92400e]"><span class="font-bold">During this period</span>, you retain the option to cancel your deletion request within your account settings.</li>
                                    <li className="text-[#92400e]">If you cancel, the deletion process will be halted, and your account will remain active.</li>
                                </ul>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Games Selection */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-medium text-[#92400e]">
                                        Select Games (Optional)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleSelectAll}
                                        className="text-xs px-3 py-1 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-[#78350f] font-medium"
                                    >
                                        {selectedGames.length === Object.values(GAMES).length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="bg-white/10 border border-white/30 rounded-lg p-4 max-h-60 overflow-y-auto">
                                    <div className="space-y-2">
                                        {Object.values(GAMES).map((game) => (
                                            <label
                                                key={game.id}
                                                className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-white/10 transition"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedGames.includes(game.id)}
                                                    onChange={() => handleGameToggle(game.id)}
                                                    className="w-5 h-5 rounded border-white/40 bg-white/20 text-red-600 focus:ring-2 focus:ring-red-500/20 focus:ring-offset-0"
                                                />
                                                <span className="text-[#78350f] text-sm font-medium">{game.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <p className="mt-2 text-xs text-[#92400e]/70">
                                    {selectedGames.length === 0
                                        ? "Leave unselected to unsubscribe from all games, or select specific games to unsubscribe only from those."
                                        : `Selected ${selectedGames.length} game(s): ${selectedGames.map(id => Object.values(GAMES).find(g => g.id === id)?.name).join(", ")}`
                                    }
                                </p>
                            </div>

                            {/* User ID Input */}
                            <div>
                                <label className="block text-sm font-medium text-[#92400e] mb-2">
                                    User ID <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#78350f]" />
                                    <input
                                        type="text"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition text-[#78350f] placeholder-[#92400e]/70"
                                        placeholder="Enter your user ID"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-[#92400e]/70">(Email or User ID required)</p>
                            </div>

                            {/* Email Input */}
                            <div>
                                <label className="block text-sm font-medium text-[#92400e] mb-2">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#78350f]" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition text-[#78350f] placeholder-[#92400e]/70"
                                        placeholder="your.email@example.com"
                                    />
                                </div>
                                <p className="mt-1 text-xs text-[#92400e]/70">(Email or User ID required)</p>
                            </div>

                            {/* Consent Checkbox */}
                            <div className="bg-red-900/10 border border-red-500/40 rounded-lg p-4">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => {
                                            setConsent(e.target.checked);
                                            setError("");
                                        }}
                                        className="mt-1 w-5 h-5 rounded border-white/40 bg-white/20 text-red-600 focus:ring-2 focus:ring-red-500/20 focus:ring-offset-0 focus:ring-offset-transparent"
                                    />
                                    <div className="flex-1">
                                        <p className="text-[#78350f] font-semibold mb-2">
                                            I hereby consent to unsubscribe from TipTideGames emails
                                        </p>
                                        <p className="text-[#92400e] text-sm leading-relaxed">
                                            By completing this form, you affirm your full and final consent for TipTide to permanently
                                            and completely delete all associated data from its systems after the 10-day retention period has concluded.
                                        </p>
                                        <ul className="list-disc list-inside space-y-1 ml-2 leading-relaxed">
                                            <li className="text-[#92400e]"><span class="font-bold">
                                                This action is immediate and irreversible.</span> you can resubscribe at any time through our <Link to="/contact" className="text-[#78350f] hover:text-[#92400e] underline">contact page</Link>.
                                            </li>
                                        </ul>
                                    </div>
                                </label>
                            </div>

                            {/* Data Retention Notice */}
                            <div className="bg-white/20 border border-white/40 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <FiInfo className="text-[#78350f] text-xl flex-shrink-0 mt-0.5" />
                                    <div className="text-[#92400e] text-sm">
                                        <p className="font-semibold mb-1 text-[#78350f]">Key Information: The 10-Day Retention Window</p>
                                        <p className="leading-relaxed">
                                            <p className="font-semibold mb-1"></p>
                                            <p className="text-[#92400e]">
                                                Please note that a <span class="font-semibold">10-day data retention period</span> is in effect starting now.
                                            </p>
                                            <ul className="list-disc list-inside space-y-1 ml-2">
                                                <li className="text-[#92400e]"><span class="font-bold">During this period</span>, you retain the option to cancel your deletion request within your account settings.</li>
                                                <li className="text-[#92400e]">If you cancel, the deletion process will be halted, and your account will remain active.</li>
                                            </ul>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="p-4 bg-red-700/30 border border-red-500/50 rounded-lg text-red-700 text-sm flex items-start gap-3">
                                    <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={!consent || isSubmitting || (!email && !userId)}
                                className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg transition font-semibold flex items-center justify-center gap-2 text-white"
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="animate-spin">⏳</span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <FiMail />
                                        Unsubscribe
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Alternative Actions */}
                        <div className="mt-8 pt-6 border-t border-white/40 text-center">
                            <p className="text-[#92400e] text-sm mb-3">
                                Changed your mind or need help?
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Link
                                    to="/contact"
                                    className="px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-sm text-[#78350f] font-medium"
                                >
                                    Contact Support
                                </Link>
                                <Link
                                    to="/"
                                    className="px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-sm text-[#78350f] font-medium"
                                >
                                    Back to Home
                                </Link>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Success State */
                    <div className="bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-8 md:p-12 shadow-lg border border-white/40 text-center backdrop-blur-sm">
                        <div className="inline-block p-4 bg-green-700/20 border border-green-500/50 rounded-full mb-6">
                            <FiCheckCircle className="w-12 h-12 text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 text-green-700">
                            Unsubscribed Successfully
                        </h2>
                        <p className="text-[#92400e] text-lg mb-2">
                            {email && <span className="font-semibold text-[#78350f]">{email}</span>}
                            {email && userId && <span className="text-[#92400e]"> and </span>}
                            {userId && <span className="font-semibold text-[#78350f]">User ID: {userId}</span>}
                            {!email && !userId && <span className="font-semibold text-[#78350f]">Your account</span>} has been removed from our mailing list.
                        </p>
                        {selectedGames.length > 0 && (
                            <p className="text-[#92400e] text-sm mb-2">
                                Unsubscribed from: <span className="font-semibold text-[#78350f]">
                                    {selectedGames.map(id => Object.values(GAMES).find(g => g.id === id)?.name).join(", ")}
                                </span>
                            </p>
                        )}
                        {selectedGames.length === 0 && (
                            <p className="text-[#92400e] text-sm mb-2">
                                You will no longer receive marketing emails from all TipTide Games.
                            </p>
                        )}
                        <p className="text-[#92400e] text-sm mb-8">
                            {selectedGames.length > 0
                                ? "You will continue to receive emails from other games you're subscribed to."
                                : "You will no longer receive marketing emails from TipTide Games."
                            }
                        </p>

                        {/* Important Info */}
                        <div className="bg-white/20 border border-white/40 rounded-lg p-4 mb-6 text-left">
                            <div className="flex items-start gap-3">
                                <FiInfo className="text-[#78350f] text-xl flex-shrink-0 mt-0.5" />
                                <div className="text-[#92400e] text-sm">
                                    <p className="font-semibold mb-1 text-[#78350f]">What happens next?</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                        <li>Your email has been removed from our marketing list immediately</li>
                                        <li>You may still receive transactional emails related to your account</li>
                                        <li>You can resubscribe anytime by contacting us</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link
                                to="/"
                                className="px-6 py-3 bg-white hover:bg-[#fffbeb] rounded-lg transition font-semibold text-[#d97706] shadow-lg shadow-white/30"
                            >
                                Return to Home
                            </Link>
                            <Link
                                to="/contact"
                                className="px-6 py-3 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition font-semibold text-[#78350f]"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Unsubscribe;